import type { SystemUserQuerySchema } from '@/_pages/system-users/shared';
import type { PhoneNumberQuerySchema } from '@/_schemas/phone-numbers';

import { currencyFormat } from './currency-format';
import { isObject } from './merge-deep';

export function numberFormat(x: number) {
  if (x) {
    return x.toLocaleString('en-US');
  }
  return x;
}

export type SearchParamDefault = Record<string, string | number | boolean | undefined>;
export type SearchParams<T extends SearchParamDefault = SearchParamDefault> = {
  [K in keyof T]: T[K];
};

export function paramsToString(params: SearchParams) {
  const queryString = new URLSearchParams();
  Object.entries(params)
    .filter(([_, value]) => Boolean(value))
    .forEach(([key, value]) => queryString.append(key, String(value)));
  return queryString.toString() ? `?${queryString}` : '';
}

export function noop() {}

export function keyBy<T, K extends string | number>(array: T[], iteratee: (value: T) => K): Record<K, T> {
  return array.reduce(
    (result, value) => {
      const key = iteratee(value);

      result[key] = value;
      return result;
    },
    {} as Record<K, T>
  );
}

export function isEqual<T>(obj1: T, obj2: T): boolean {
  if (Object.is(obj1, obj2)) {
    return true;
  }

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  const entries1 = Object.entries(obj1 as Record<string, unknown>);
  const entries2 = Object.entries(obj2 as Record<string, unknown>);

  if (entries1.length !== entries2.length) {
    return false;
  }

  return entries1.every(([key, value]) => {
    if (!(key in obj2)) {
      return false;
    }

    const value2 = (obj2 as Record<string, unknown>)[key];
    const areObjects = isObject(value) && isObject(value2);

    return (areObjects && isEqual(value, value2)) || (!areObjects && Object.is(value, value2));
  });
}

export function isEqualArray<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!isEqual(arr1[i], arr2[i])) {
      return false;
    }
  }

  return true;
}

export function sortByLatest<T extends { updated_at: string; id: number }>(data: T[]): T[] {
  return data.sort((a, b) => {
    const updatedAtA = new Date(a.updated_at);
    const updatedAtB = new Date(b.updated_at);

    if (updatedAtA > updatedAtB && a.id < b.id) {
      return -1;
    }
    if (updatedAtA < updatedAtB && a.id > b.id) {
      return 1;
    }

    return 0;
  });
}

export function nameResolver(first_name: string | null | undefined, last_name: string | null | undefined, fill = '—') {
  return `${first_name ?? ''} ${last_name ?? ''}`.trim() || fill;
}

export function getUniqueBy<T>(arr: T[], prop: keyof T) {
  const set = new Set();
  return arr.filter((o) => !set.has(o[prop]) && set.add(o[prop]));
}

export function toNumberOnly(value: string) {
  return value.replace(/\D/g, '');
}

export function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function getCallableNumber(phoneNumber: PhoneNumberQuerySchema, user?: SystemUserQuerySchema | null) {
  const US_CANADA_COUNTRIES = ['US', 'CA'];
  if (
    user &&
    user.country &&
    US_CANADA_COUNTRIES.includes(user.country?.short_name ?? '') &&
    !US_CANADA_COUNTRIES.includes(phoneNumber.country?.short_name ?? '')
  ) {
    const number = phoneNumber.number.startsWith('+') ? phoneNumber.number.slice(1) : phoneNumber.number;
    return `011${number}`;
  }
  return phoneNumber.number;
}

export function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

/**
 * Formats a number into a compact currency string with appropriate suffix (T, B, M, K)
 * @param value - The number to format
 * @returns Formatted currency string
 * @example
 * formatCompactCurrency(1500000000000) // "$1.5T"
 * formatCompactCurrency(2500000000) // "$2.5B"
 * formatCompactCurrency(3500000) // "$3.5M"
 * formatCompactCurrency(450000) // "$450K"
 * formatCompactCurrency(5000) // "$5,000.00"
 */
export function formatCompactCurrency(value: number): string {
  if (value >= 1000000000000) {
    return `$${(value / 1000000000000).toFixed(1)}T`;
  }
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 100000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return currencyFormat(value);
}

export function parsePostalCode(value: string) {
  return encodeURIComponent(value);
}

export function stripHtml(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent ?? '';
}

export function isAdminUser(roles: SystemUserQuerySchema['roles'], allowedRoles: string[] = []): boolean {
  return Boolean(
    roles &&
    roles.length > 0 &&
    roles.some((role) => {
      const roleName = role.name?.toLowerCase();
      return [...(allowedRoles || []).map((role) => role.toLowerCase()), 'admin', 'executive', 'ceo', 'director'].some(
        (name) => roleName?.includes(name)
      );
    })
  );
}
